/******************************************************************************
 * FILE: src/modules/uploads/services/uploadService.js
 * MODULE: Generic Upload Framework
 * SERVICE: Upload Service
 *
 * PURPOSE:
 * Frontend API client for the Generic Upload Framework.
 *
 * RESPONSIBILITIES:
 * - Download Upload Templates
 * - Upload Excel Files
 * - Start Import
 * - Get Upload Status
 * - Get Upload History
 * - Get Upload Details
 * - Download Error File
 * - Download Success File
 *
 * BACKEND:
 * /api/uploads
 *
 * AUTHOR:
 * DizyPro ERP Development Team
 ******************************************************************************/

import axiosClient from "../../../api/axiosClient";

/******************************************************************************
 * Constants
 ******************************************************************************/

const BASE_URL = "/api/uploads";

/******************************************************************************
 * Helpers
 ******************************************************************************/

/**
 * Download file from blob response.
 */
/**
 * Download file from blob response.
 *
 * Preserves the server-provided MIME type and resolves the
 * filename from Content-Disposition when available.
 *
 * Parameters
 * ----------
 * response : AxiosResponse
 *     Axios response containing blob data.
 *
 * fallbackFilename : string
 *     Filename to use when Content-Disposition is unavailable.
 */
function downloadBlob(
    response,
    fallbackFilename
) {
    const contentType =
        response.headers["content-type"] ||
        "application/octet-stream";

    const blob = new Blob(
        [
            response.data
        ],
        {
            type: contentType
        }
    );

    const disposition =
        response.headers["content-disposition"];

    let filename =
        fallbackFilename;

    if (disposition) {

        const encodedMatch =
            disposition.match(
                /filename\*=UTF-8''([^;]+)/i
            );

        if (encodedMatch) {

            try {

                filename =
                    decodeURIComponent(
                        encodedMatch[1]
                    );

            } catch (error) {

                filename =
                    encodedMatch[1];
            }

        } else {

            const filenameMatch =
                disposition.match(
                    /filename="?([^"]+)"?/i
                );

            if (filenameMatch) {

                filename =
                    filenameMatch[1];
            }
        }
    }

    const url =
        window.URL.createObjectURL(
            blob
        );

    const link =
        document.createElement(
            "a"
        );

    link.href = url;

    link.download =
        filename;

    document.body.appendChild(
        link
    );

    link.click();

    link.remove();

    window.URL.revokeObjectURL(
        url
    );
}
/******************************************************************************
 * DOWNLOAD TEMPLATE
 *
 * Backend:
 * GET /api/uploads/template
 ******************************************************************************/

async function downloadTemplate({
    moduleCode,
    entityName,
    uploadType
}) {
    const response =
        await axiosClient.get(
            `${BASE_URL}/template`,
            {
                params: {
                    module_code:
                        moduleCode,

                    entity_name:
                        entityName,

                    upload_type:
                        uploadType
                },

                responseType:
                    "blob"
            }
        );

    downloadBlob(
        response,
        "Engineering_SKU_Template.xlsx"
    );
}

/******************************************************************************
 * UPLOAD FILE
 *
 * Backend:
 * POST /api/uploads/upload
 ******************************************************************************/

async function uploadFile({

    moduleCode,

    entityName,

    uploadType,

    file

}) {

    const formData = new FormData();

    formData.append(
        "module_code",
        moduleCode
    );

    formData.append(
        "entity_name",
        entityName
    );

    formData.append(
        "upload_type",
        uploadType
    );

    formData.append(
          "file",
            file
    );

    const response = await axiosClient.post(

        `${BASE_URL}/upload`,

        formData,

        {

            headers: {

                "Content-Type":
                    "multipart/form-data"

            }

        }

    );

    return response.data.data;

}
/******************************************************************************
 * IMPORT UPLOAD
 *
 * Backend:
 * POST /api/uploads/import/{jobId}
 ******************************************************************************/

async function importUpload(jobId) {

    const response = await axiosClient.post(

        `${BASE_URL}/import/${jobId}`

    );

    return response.data.data;

}

/******************************************************************************
 * GET UPLOAD STATUS
 *
 * Backend:
 * GET /api/uploads/status/{jobId}
 ******************************************************************************/

async function getUploadStatus(jobId) {

    const response = await axiosClient.get(

        `${BASE_URL}/status/${jobId}`

    );

    return response.data.data;

}

/******************************************************************************
 * GET UPLOAD HISTORY
 *
 * Backend:
 * GET /api/uploads/history
 ******************************************************************************/

async function getUploadHistory({

    moduleCode,

    entityName,

    page = 1,

    perPage = 20

} = {}) {

    const response = await axiosClient.get(

        `${BASE_URL}/history`,

        {

            params: {

                module_code: moduleCode,

                entity_name: entityName,

                page,

                per_page: perPage

            }

        }

    );

    const payload = response.data?.data || {};

    return {

        items: payload.data || [],

        pagination: payload.pagination || {}

    };

}

/******************************************************************************
 * GET UPLOAD DETAILS
 *
 * Backend:
 * GET /api/uploads/{jobId}
 ******************************************************************************/

async function getUploadDetails(jobId) {

    const response = await axiosClient.get(

        `${BASE_URL}/${jobId}`

    );

    return response.data.data;

}
/******************************************************************************
 * DOWNLOAD ERROR FILE
 *
 * Backend:
 * GET /api/uploads/{jobId}/error-file
 ******************************************************************************/

async function downloadErrorFile(jobId) {

    const response = await axiosClient.get(

        `${BASE_URL}/${jobId}/error-file`,

        {

            responseType: "blob"

        }

    );

    downloadBlob(
        response,
        "Upload_Error_File.xlsx"
    );

}

/******************************************************************************
 * DOWNLOAD SUCCESS FILE
 *
 * Backend:
 * GET /api/uploads/{jobId}/success-file
 ******************************************************************************/

async function downloadSuccessFile(jobId) {

    const response = await axiosClient.get(

        `${BASE_URL}/${jobId}/success-file`,

        {

            responseType: "blob"

        }

    );

    downloadBlob(
        response,
        "Upload_Success_File.xlsx"
    )

}

/******************************************************************************
 * EXPORT
 ******************************************************************************/

const uploadService = {

    downloadTemplate,

    uploadFile,

    importUpload,

    getUploadStatus,

    getUploadHistory,

    getUploadDetails,

    downloadErrorFile,

    downloadSuccessFile

};

export default uploadService;